import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import { Dialog, DialogContent, InputAdornment, Skeleton, Stack, TextField, Typography } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'

type DashboardGamesDialogProps = {
  open: boolean
  title: ReactNode
  onClose: () => void
  search: string
  onSearchChange: (search: string) => void
  // Shown next to the search, such as an analyst filter.
  filters?: ReactNode
  // A line above the list, such as its count.
  summary?: ReactNode
  isLoading?: boolean
  error?: string
  children?: ReactNode
}

export const DashboardGamesDialog: FC<DashboardGamesDialogProps> = ({
  open,
  title,
  onClose,
  search,
  onSearchChange,
  filters,
  summary,
  isLoading,
  error,
  children,
}) => {
  const { t } = useTranslation()

  const renderContent = () => {
    if (isLoading) return <Skeleton variant="rectangular" height={320} />
    if (error) return <Typography color="error.main">{error}</Typography>
    return (
      <>
        {summary}
        {children}
      </>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <GRDialogTitle onClose={onClose}>{title}</GRDialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={t('internal-analysts:dashboard_search_games')}
              inputProps={{ 'aria-label': t('internal-analysts:dashboard_search_games') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            {filters}
          </Stack>
          {renderContent()}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
